import { useState } from 'react'
import dayjs from 'dayjs'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { formatDayLabel } from '@/lib/dates'
import { useUpdateClientDashboard } from '@/queries/clientDashboards'
import { extractErrorMessage } from '@/queries/ticketMutations'
import { CADENCE_LABELS } from '@/types/clientDashboard'
import type { ClientDashboard } from '@/types/clientDashboard'
import { daysUntilLabel, nextCallLabel } from '@/types/clientMeetings'
import { DashCard } from './dashboardParts'
import { PrepareMeetingLink } from './PrepareMeetingLink'

export function NextCallCard({ dashboard }: { dashboard: ClientDashboard }) {
  const update = useUpdateClientDashboard(dashboard.companyId)
  const [draft, setDraft] = useState<string | null>(null)
  const label = nextCallLabel(dashboard.nextCallAt)

  async function save(nextCallAt: string | null) {
    try {
      await update.mutateAsync({ nextCallAt })
      setDraft(null)
      toast.success(nextCallAt ? 'Next call saved' : 'Next call cleared')
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not save the next call'))
    }
  }

  const startEditing = () => setDraft(label && dashboard.nextCallAt ? dayjs(dashboard.nextCallAt).format('YYYY-MM-DDTHH:mm') : '')

  return (
    <DashCard
      title="Next call"
      right={
        <div className="flex items-center gap-1.5">
          {label && dashboard.nextCallAt && <Badge variant="info">{daysUntilLabel(dashboard.nextCallAt)}</Badge>}
          {draft === null && (
            <Button size="xs" variant="outline" onClick={startEditing}>
              Edit
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-1 px-[18px] py-3.5 text-[13px]">
        {draft === null ? (
          <>
            <div data-testid="next-call-value" className={label ? 'font-semibold text-etn-text' : 'italic text-etn-text-muted'}>
              {label ?? 'Not set'}
            </div>
            {dashboard.cadence && <div className="text-[12px] text-etn-text-soft">{CADENCE_LABELS[dashboard.cadence]} check-in</div>}
            <div className="pt-2">
              <PrepareMeetingLink size="xs" />
            </div>
          </>
        ) : (
          <div className="space-y-2">
            <Input type="datetime-local" aria-label="Next call date and time" value={draft} onChange={event => setDraft(event.target.value)} />
            <div className="flex flex-wrap gap-1.5">
              <Button size="sm" disabled={!draft || update.isPending} onClick={() => save(new Date(draft).toISOString())}>
                Save
              </Button>
              <Button size="sm" variant="outline" disabled={update.isPending} onClick={() => save(null)}>
                Clear
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
        {dashboard.lastCallAt && <div className="pt-1.5 text-[12px] text-etn-text-soft">Last call {formatDayLabel(dashboard.lastCallAt)}</div>}
      </div>
    </DashCard>
  )
}
